import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Flex,
    Select,
    Spinner,
    Text,
} from '@chakra-ui/react';
import { getQuoteRevisions } from '../../api/revision-api';
import { getQuoteDiff, getCellDiff, cellDataToLookup, formatRevisionLabel, mapSnapshotToRows, mapCellSnapshotToRows, mapQuoteTableRowsToReadOnly, mapLegacyDiffKeysToCell, buildReadOnlyTotals, LEGACY_COLUMNS } from './utils';
import { calculateTotalFromRows } from '../../../components/QuoteTable/utils';
import { TotalType } from '../../../components/QuoteTable/utils';
import { ReadOnlyQuoteTable } from './components/ReadOnlyQuoteTable';
import { Modal } from 'newui/components/ui/Modal';
import type { QuoteTableColumn } from '../../../components/QuoteTable/quote-table.types';
import type { QuoteRevisionSnapshot } from '../../types/revision.type';
import type { RfxQuoteItem, RfxQuoteColumn } from '../../types/quote.type';

export interface CompareRevisionsModalProps {
    columns?: RfxQuoteColumn[];
    initialRevisionA?: number;
    initialRevisionB?: number;
    inviteId: number;
    isOpen: boolean;
    quoteId: number;
    quoteItems: RfxQuoteItem[];
    quoteName: string;
    onClose: () => void;
}

const CompareRevisionsModal: React.FC<CompareRevisionsModalProps> = ({
    isOpen,
    onClose,
    columns: quoteColumns,
    inviteId,
    quoteId,
    quoteName,
    quoteItems,
    initialRevisionA,
    initialRevisionB,
}) => {
    const hasColumns = Boolean(quoteColumns?.length);
    const tableColumns: QuoteTableColumn[] = hasColumns
        ? quoteColumns!.map((c) => ({
            id: c.id,
            columnType: c.columnType,
            label: c.name,
            sortOrder: c.sortOrder,
            isDefault: c.isClientEditable,
            isSupplierEditable: c.isSupplierEditable,
        }))
        : LEGACY_COLUMNS;
    const [revisions, setRevisions] = useState<QuoteRevisionSnapshot[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedA, setSelectedA] = useState<number | undefined>(initialRevisionA);
    const [selectedB, setSelectedB] = useState<number | undefined>(initialRevisionB);
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const fetchRevisions = useCallback(async () => {
        if (!isOpen) {
            return;
        }
        setIsLoading(true);
        try {
            const data = await getQuoteRevisions({ inviteId: inviteId, quoteId });
            setRevisions(data);

            const sorted = [...data].sort((a, b) => b.revision - a.revision);
            const maxRevision = sorted[0]?.revision ?? 0;
            const secondMax = sorted[1]?.revision ?? 0;

            setSelectedA(maxRevision);
            setSelectedB(secondMax);
        } catch {
            // Error handled by global axios interceptor
        } finally {
            setIsLoading(false);
        }
    }, [isOpen, inviteId, quoteId]);

    useEffect(() => {
        fetchRevisions();
    }, [fetchRevisions]);

    const revisionA = useMemo(
        () => revisions.find((r) => r.revision === selectedA),
        [revisions, selectedA]
    );

    const revisionB = useMemo(
        () => revisions.find((r) => r.revision === selectedB),
        [revisions, selectedB]
    );

    const rowsA = useMemo(() => {
        if (!revisionA) {
            return [];
        }
        if (hasColumns && revisionA.cells?.length) {
            return mapCellSnapshotToRows(cellDataToLookup(revisionA.cells), quoteItems);
        }
        return mapSnapshotToRows(revisionA, quoteItems);
    }, [revisionA, quoteItems, hasColumns]);

    const rowsB = useMemo(() => {
        if (!revisionB) {
            return [];
        }
        if (hasColumns && revisionB.cells?.length) {
            return mapCellSnapshotToRows(cellDataToLookup(revisionB.cells), quoteItems);
        }
        return mapSnapshotToRows(revisionB, quoteItems);
    }, [revisionB, quoteItems, hasColumns]);

    const cellDiffKeys = useMemo(() => {
        if (!revisionA || !revisionB) {
            return new Set<string>();
        }
        if (hasColumns) {
            const cellsA = cellDataToLookup(revisionA.cells ?? []);
            const cellsB = cellDataToLookup(revisionB.cells ?? []);
            return getCellDiff(cellsA, cellsB);
        }
        const legacyDiff = getQuoteDiff(revisionA, revisionB);
        return mapLegacyDiffKeysToCell(legacyDiff, LEGACY_COLUMNS);
    }, [revisionA, revisionB, hasColumns]);

    const readOnlyRowsA = useMemo(
        () => mapQuoteTableRowsToReadOnly(rowsA, tableColumns),
        [rowsA, tableColumns]
    );

    const readOnlyRowsB = useMemo(
        () => mapQuoteTableRowsToReadOnly(rowsB, tableColumns),
        [rowsB, tableColumns]
    );

    const totalsA = useMemo(
        () => hasColumns ? undefined : buildReadOnlyTotals(calculateTotalFromRows(rowsA, TotalType.SubTotal)),
        [rowsA, hasColumns]
    );

    const totalsB = useMemo(
        () => hasColumns ? undefined : buildReadOnlyTotals(calculateTotalFromRows(rowsB, TotalType.SubTotal)),
        [rowsB, hasColumns]
    );

    const hasMultipleRevisions = revisions.length >= 3;

    const handleSelectA = (value: number) => {
        if (value === selectedB) {
            const adjacent = value > 0 ? value - 1 : value + 1;
            setSelectedB(adjacent);
        }
        setSelectedA(value);
    };

    const handleSelectB = (value: number) => {
        if (value === selectedA) {
            const adjacent = value > 0 ? value - 1 : value + 1;
            setSelectedA(adjacent);
        }
        setSelectedB(value);
    };

    const labelA = hasMultipleRevisions
        ? formatRevisionLabel(selectedA ?? 0)
        : `${formatRevisionLabel((revisions[1]?.revision) ?? 1)} (Latest)`;

    const labelB = hasMultipleRevisions
        ? formatRevisionLabel(selectedB ?? 0)
        : 'Original';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="full"
            title="Compare Revisions"
            description={`for '${quoteName}'`}
            modalType="standard"
            iconType="info"
            closeOnEsc
            closeOnOverlayClick
            secondaryButton={{
                onClick: onClose,
                text: "Close",
                size: "sm",
                width: "full"
            }}
            wrapDescription
        >
            {isLoading ? (
                <Flex
                    justify="center"
                    align="center"
                    py={8}
                >
                    <Spinner size="lg" />
                </Flex>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {revisionA && (
                            <div>
                                <p className="mb-3 text-base font-semibold text-gray-700">
                                    {hasMultipleRevisions ? <Select
                                        value={selectedA ?? ''}
                                        onChange={(e) => handleSelectA(Number(e.target.value))}
                                        data-testid="compare-select-a"
                                        fontSize={"sm"}
                                        width={150}
                                    >
                                        {revisions.map((r) => (
                                            <option
                                                key={r.revision}
                                                value={r.revision}
                                            >
                                                {formatRevisionLabel(r.revision)}
                                            </option>
                                        ))}
                                    </Select> : labelA}
                                </p>
                                <ReadOnlyQuoteTable
                                    columns={tableColumns}
                                    rows={readOnlyRowsA}
                                    totals={totalsA}
                                    diffKeys={cellDiffKeys}
                                    hoveredRowIndex={hoveredRowIndex}
                                    onRowHover={setHoveredRowIndex}
                                />
                            </div>
                        )}

                        {revisionB && (
                            <div>
                                <p className="mb-3 text-base font-semibold text-gray-500">
                                    {hasMultipleRevisions ? <Select
                                        value={selectedB ?? ''}
                                        onChange={(e) => handleSelectB(Number(e.target.value))}
                                        data-testid="compare-select-b"
                                        fontSize={"sm"}
                                        width={150}
                                    >
                                        {revisions.map((r) => (
                                            <option
                                                key={r.revision}
                                                value={r.revision}
                                            >
                                                {formatRevisionLabel(r.revision)}
                                            </option>
                                        ))}
                                    </Select> : labelB}
                                </p>
                                <ReadOnlyQuoteTable
                                    columns={tableColumns}
                                    rows={readOnlyRowsB}
                                    totals={totalsB}
                                    hoveredRowIndex={hoveredRowIndex}
                                    muted
                                    onRowHover={setHoveredRowIndex}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
        </Modal>
    );
};

export { CompareRevisionsModal };
